import React, { useState } from 'react';

interface SampleOutput {
  id: string;
  name: string;
  vendor: string;
  rawText: string;
  parsedSchema: {
    deviceType: string;
    protocol: string;
    extractedMetrics: { key: string; val: string; status: 'ok' | 'warn' | 'alert' }[];
    generatedRules: string[];
  };
}

const SAMPLES: SampleOutput[] = [
  {
    id: 'cisco',
    name: 'Cisco Core ASR1000 - Transceiver Detail',
    vendor: 'Cisco IOS-XE',
    rawText: `ASR1000-PE-01# show interfaces TenGigE0/0/0 transceiver detail
Optical Tx Power: -2.1 dBm (Normal)
Optical Rx Power: -18.4 dBm (WARNING: Threshold Low -17.0 dBm)
Temperature: 42.5 C (Normal)
VCC Voltage: 3.28 V
Laser Bias Current: 32.1 mA
Module SN: FNS22190A9Z`,
    parsedSchema: {
      deviceType: 'Core Edge Router / Optical SFP+',
      protocol: 'CLI (SSH) -> MUSE Schema Model v2',
      extractedMetrics: [
        { key: 'optical_tx_power_dbm', val: '-2.1 dBm', status: 'ok' },
        { key: 'optical_rx_power_dbm', val: '-18.4 dBm', status: 'alert' },
        { key: 'chassis_temp_celsius', val: '42.5 °C', status: 'ok' },
        { key: 'laser_bias_ma', val: '32.1 mA', status: 'ok' }
      ],
      generatedRules: [
        'ALERT IF optical_rx_power_dbm < -17.0 (Optical degradation imminent)',
        'POLLED_INTERVAL: 60s via gRPC/SSH parser',
        'TOPOLOGY_LINK: Connect to Core-Agg-Trunk-01'
      ]
    }
  },
  {
    id: 'juniper',
    name: 'Juniper MX480 - BGP Summary Stanza',
    vendor: 'Juniper JunOS',
    rawText: `noc@junos-mx480-core> show bgp summary
Groups: 4 Peers: 12 Down peers: 1
Peer                     AS      InPkt     OutPkt    OutQ   Flaps Last Up/Dwn State|#Active/Received/Accepted/Damped
198.51.100.1          64501    1840292    1920194       0       3     2w4d 14/14/14/0
203.0.113.45          65100       1204       1190       0      14     00:04:12 Active (Session Flap)`,
    parsedSchema: {
      deviceType: 'Core Peering Router / BGP Engine',
      protocol: 'JunOS CLI -> MUSE Peering Parser',
      extractedMetrics: [
        { key: 'bgp_peer_address', val: '203.0.113.45', status: 'ok' },
        { key: 'bgp_peer_as', val: '65100', status: 'ok' },
        { key: 'bgp_session_state', val: 'Active (Flapping)', status: 'alert' },
        { key: 'bgp_flap_count', val: '14 flaps', status: 'warn' }
      ],
      generatedRules: [
        'ALERT IF bgp_session_state != Established',
        'CORRELATE WITH: Shared IXP Peering Port 4/1',
        'NOTIFY: NOC Escalation Tier 1'
      ]
    }
  },
  {
    id: 'mikrotik',
    name: 'MikroTik CCR2116 - Interface Monitor',
    vendor: 'RouterOS v7',
    rawText: `/interface monitor-traffic sfp-sfpplus1
            name: sfp-sfpplus1
   rx-packets-per-second: 482,109
     rx-bits-per-second: 4.8Gbps
   tx-packets-per-second: 112,044
     tx-bits-per-second: 1.1Gbps
               fp-rx-pps: 0
               fp-tx-pps: 0
         rx-drops-per-sec: 14,022 (PACKET DROP SURGE)`,
    parsedSchema: {
      deviceType: 'Access Aggregation Switch',
      protocol: 'RouterOS API / CLI Stream',
      extractedMetrics: [
        { key: 'interface_name', val: 'sfp-sfpplus1', status: 'ok' },
        { key: 'rx_bandwidth_gbps', val: '4.8 Gbps', status: 'ok' },
        { key: 'tx_bandwidth_gbps', val: '1.1 Gbps', status: 'ok' },
        { key: 'rx_packet_drops_pps', val: '14,022 pps', status: 'alert' }
      ],
      generatedRules: [
        'WARN IF rx_packet_drops_pps > 1,000 (Ring Congestion)',
        'AUTO_MAP: Customer Edge Segment B',
        'POLLED_INTERVAL: 30s via RouterOS REST API'
      ]
    }
  }
];

export const MuseForgePlayground: React.FC = () => {
  const [activeSampleId, setActiveSampleId] = useState<string>('cisco');
  const [customText, setCustomText] = useState<string>(SAMPLES[0].rawText);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [schemaReady, setSchemaReady] = useState<boolean>(true);

  const activeSample = SAMPLES.find((s) => s.id === activeSampleId) || SAMPLES[0];

  const handleSelectSample = (sample: SampleOutput) => {
    setActiveSampleId(sample.id);
    setCustomText(sample.rawText);
    setSchemaReady(true);
  };

  const handleRunAnalysis = () => {
    setIsAnalyzing(true);
    setSchemaReady(false);
    setTimeout(() => {
      setIsAnalyzing(false);
      setSchemaReady(true);
    }, 500);
  };

  return (
    <div className="flat-card forge-minimal">
      <div className="forge-top-head">
        <div>
          <div className="pill-tag mb-2">
            <span className="status-dot green" />
            <span>MUSE FORGE PARSER</span>
          </div>
          <h3>Teach the Platform Once. Monitor Continuously.</h3>
          <p className="subtext">
            Paste raw CLI output or SNMP walks to generate normalized telemetry schemas.
          </p>
        </div>

        <div className="preset-selector">
          {SAMPLES.map((sample) => (
            <button
              key={sample.id}
              onClick={() => handleSelectSample(sample)}
              className={`preset-btn ${activeSampleId === sample.id ? 'active' : ''}`}
            >
              {sample.vendor}
            </button>
          ))}
        </div>
      </div>

      {/* 2-Column Top Workspace */}
      <div className="grid-2 forge-workspace">
        {/* Terminal Input */}
        <div className="pane-box">
          <div className="pane-header">
            <div className="header-left">
              <span className="status-dot blue" />
              <span className="pane-title">Raw Terminal Input</span>
            </div>
            <button onClick={handleRunAnalysis} className="btn btn-secondary btn-xs" disabled={isAnalyzing}>
              {isAnalyzing ? 'Parsing...' : 'Parse CLI Output'}
            </button>
          </div>
          <div className="pane-content pane-editor-wrap">
            <textarea
              className="pane-editor"
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value);
                setSchemaReady(false);
              }}
            />
          </div>
        </div>

        {/* Generated Schema Output */}
        <div className="pane-box">
          <div className="pane-header">
            <div className="header-left">
              <span className="status-dot green" />
              <span className="pane-title">Normalized Schema</span>
            </div>
            <span className="schema-badge">VERIFIED</span>
          </div>

          <div className="pane-content">
            {schemaReady ? (
              <div className="schema-body">
                <div className="schema-info-row">
                  <div>
                    <span className="info-lbl">Device Category</span>
                    <span className="info-val">{activeSample.parsedSchema.deviceType}</span>
                  </div>
                  <div>
                    <span className="info-lbl">Protocol</span>
                    <span className="info-val">{activeSample.parsedSchema.protocol}</span>
                  </div>
                </div>

                <div className="metrics-section">
                  <span className="section-title">EXTRACTED TELEMETRY METRICS</span>
                  {activeSample.parsedSchema.extractedMetrics.map((m, idx) => (
                    <div key={idx} className="metric-item">
                      <span className="m-key">{m.key}</span>
                      <span className="m-val">{m.val}</span>
                      <span className={`status-dot ${m.status === 'ok' ? 'green' : m.status === 'warn' ? 'amber' : 'red'}`} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="idle-prompt">
                <p>Click <strong>"Parse CLI Output"</strong> to extract metrics.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full-Width Joined Section: PARSER RULES */}
      <div className="pane-box forge-rules-panel">
        <div className="pane-header">
          <div className="header-left">
            <span className="status-dot amber" />
            <span className="pane-title">PARSER RULES</span>
          </div>
          <span className="rules-count-badge">
            {schemaReady ? `${activeSample.parsedSchema.generatedRules.length} POLICIES ACTIVE` : 'AWAITING PARSE'}
          </span>
        </div>

        <div className="rules-content">
          {schemaReady ? (
            <div className="rules-grid">
              {activeSample.parsedSchema.generatedRules.map((rule, idx) => {
                let ruleType = 'RULE';
                let tagClass = 'tag-rule';
                if (rule.startsWith('ALERT')) {
                  ruleType = 'ALERT THRESHOLD';
                  tagClass = 'tag-alert';
                } else if (rule.startsWith('WARN')) {
                  ruleType = 'WARNING THRESHOLD';
                  tagClass = 'tag-warn';
                } else if (rule.startsWith('POLLED')) {
                  ruleType = 'POLLING CADENCE';
                  tagClass = 'tag-cadence';
                } else if (rule.startsWith('TOPOLOGY') || rule.startsWith('AUTO_MAP') || rule.startsWith('CORRELATE')) {
                  ruleType = 'TOPOLOGY MAPPING';
                  tagClass = 'tag-topo';
                } else if (rule.startsWith('NOTIFY')) {
                  ruleType = 'ROUTING ACTION';
                  tagClass = 'tag-action';
                }

                return (
                  <div key={idx} className="rule-card">
                    <div className="rule-card-top">
                      <span className={`rule-type-badge ${tagClass}`}>{ruleType}</span>
                      <span className="rule-index font-mono">0{idx + 1}</span>
                    </div>
                    <div className="rule-text font-mono">
                      {rule}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="idle-rules-prompt">
              <span>Execute parser above to generate schema rules and topology bindings.</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .forge-minimal {
          margin: 2rem 0;
        }

        .forge-top-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1.5rem;
          margin-bottom: 1.75rem;
          flex-wrap: wrap;
        }

        .forge-top-head h3 {
          font-size: 1.4rem;
          margin-bottom: 0.3rem;
        }

        .subtext {
          font-size: 0.88rem;
          color: var(--text-muted);
        }

        .preset-selector {
          display: flex;
          gap: 0.4rem;
        }

        .preset-btn {
          background: #111111;
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          font-size: 0.78rem;
          padding: 0.4rem 0.8rem;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .preset-btn:hover {
          color: #ffffff;
          border-color: #333333;
        }

        .preset-btn.active {
          background: #ffffff;
          color: #000000;
          font-weight: 600;
          border-color: #ffffff;
        }

        .forge-workspace {
          align-items: stretch;
        }

        .pane-box {
          background: #050505;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .pane-header {
          background: #0d0d0d;
          border-bottom: 1px solid var(--border-color);
          padding: 0.6rem 0.85rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pane-title {
          font-family: var(--font-mono);
          font-size: 0.75rem;
          font-weight: 600;
          color: #ffffff;
          letter-spacing: 0.03em;
        }

        .btn-xs {
          padding: 0.25rem 0.65rem;
          font-size: 0.75rem;
        }

        .pane-editor-wrap {
          padding: 0 !important;
          display: flex;
          flex: 1;
        }

        .pane-editor {
          width: 100%;
          min-height: 220px;
          height: 100%;
          background: #050505;
          color: #d4d4d4;
          font-family: var(--font-mono);
          font-size: 0.78rem;
          padding: 0.9rem;
          border: none;
          outline: none;
          resize: none;
          line-height: 1.5;
        }

        .pane-content {
          padding: 0.9rem;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .schema-body {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          flex: 1;
        }

        .schema-info-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          background: #0c0c0c;
          padding: 0.55rem 0.75rem;
          border-radius: 4px;
          border: 1px solid var(--border-color);
        }

        .info-lbl {
          display: block;
          font-size: 0.65rem;
          color: var(--text-dim);
          text-transform: uppercase;
          font-family: var(--font-mono);
          margin-bottom: 0.15rem;
        }

        .info-val {
          font-size: 0.78rem;
          color: #ffffff;
          font-weight: 600;
        }

        .metrics-section {
          flex: 1;
        }

        .section-title {
          display: block;
          font-family: var(--font-mono);
          font-size: 0.65rem;
          color: var(--text-dim);
          margin-bottom: 0.4rem;
          letter-spacing: 0.04em;
        }

        .metric-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-family: var(--font-mono);
          font-size: 0.75rem;
          padding: 0.32rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .metric-item:last-child {
          border-bottom: none;
        }

        .m-key { color: var(--text-muted); }
        .m-val { color: #ffffff; font-weight: 500; }

        .schema-badge {
          font-family: var(--font-mono);
          font-size: 0.65rem;
          color: var(--text-dim);
          border: 1px solid var(--border-color);
          padding: 0.1rem 0.4rem;
          border-radius: 3px;
        }

        /* Joined Bottom Panel for Rules */
        .forge-rules-panel {
          margin-top: 1rem;
        }

        .rules-count-badge {
          font-family: var(--font-mono);
          font-size: 0.65rem;
          color: var(--text-muted);
          background: #111111;
          padding: 0.15rem 0.5rem;
          border-radius: 3px;
          border: 1px solid var(--border-color);
        }

        .rules-content {
          padding: 0.85rem;
        }

        .rules-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 0.75rem;
        }

        .rule-card {
          background: #0a0a0a;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 4px;
          padding: 0.75rem 0.85rem;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
          transition: all 0.2s ease;
        }

        .rule-card:hover {
          border-color: rgba(255, 255, 255, 0.18);
          background: #0e0e0e;
        }

        .rule-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .rule-type-badge {
          font-family: var(--font-mono);
          font-size: 0.62rem;
          font-weight: 600;
          padding: 0.15rem 0.45rem;
          border-radius: 3px;
          letter-spacing: 0.04em;
        }

        .tag-alert {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.25);
        }

        .tag-warn {
          background: rgba(245, 158, 11, 0.12);
          color: #fbbf24;
          border: 1px solid rgba(245, 158, 11, 0.25);
        }

        .tag-cadence {
          background: rgba(59, 130, 246, 0.12);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.25);
        }

        .tag-topo {
          background: rgba(16, 185, 129, 0.12);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.25);
        }

        .tag-action {
          background: rgba(168, 85, 247, 0.12);
          color: #c084fc;
          border: 1px solid rgba(168, 85, 247, 0.25);
        }

        .tag-rule {
          background: rgba(255, 255, 255, 0.06);
          color: #e5e5e5;
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        .rule-index {
          font-size: 0.65rem;
          color: var(--text-dim);
        }

        .rule-text {
          font-size: 0.76rem;
          color: #e2e8f0;
          line-height: 1.45;
          word-break: break-word;
        }

        .idle-prompt, .idle-rules-prompt {
          height: 100%;
          min-height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          font-size: 0.85rem;
        }
      `}</style>
    </div>
  );
};
